import { Component, inject, signal, OnInit, computed, DestroyRef, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PromptBotService } from '../../../../core/services/prompt-bot.service';
import { Feature, FeatureOption, TableActionEvent } from '../../../../core/models/prompt-bot.model';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { InputComponent } from '../../../../shared/components/ui/input/input.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { PaginatedTableComponent, TableHeader } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { PromptDetailModalComponent, PromptDetailData } from '../../../../shared/components/prompt-detail-modal/prompt-detail-modal.component';
import { IconComponent, IconName } from '../../../../shared/icons/icon.component';
import { AuditService } from '../../../../core/services/audit.service';
import { AuditAction, AuditEntityType } from '../../../../core/models/audit-log.model';

@Component({
  selector: 'app-prompt-options',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, InputComponent, AlertComponent, PaginatedTableComponent, DropdownComponent, MatDialogModule, IconComponent],
  templateUrl: './options.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./options.component.scss']
})
export class PromptOptionsComponent implements OnInit {
  private botService = inject(PromptBotService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private auditService = inject(AuditService);

  @Output() actionChange = new EventEmitter<void>();

  features = signal<Feature[]>([]);
  options = signal<FeatureOption[]>([]);
  selectedFeatureId = signal<number | null>(null);
  isEditMode = signal(false);
  successMessage = signal('');

  currentOption: FeatureOption = this.defaultOption();
  private previousOptionState: FeatureOption | null = null;

  featureDropdownOptions = computed<DropdownOption[]>(() =>
    this.features().map(f => ({ value: String(f.id!), label: f.name }))
  );

  headers: TableHeader[] = [
    { label: 'Option Label', key: 'name', align: 'text-left' },
    { label: 'Key', key: 'keyname', align: 'text-left' },
    { label: 'Description', key: 'display_description', align: 'text-left' },
    { label: 'Status', key: 'is_active', align: 'text-center', sortable: false },
  ];

  actionOptions = [
    { label: 'View Details', value: 'view', iconName: 'eye' as IconName, isShow: true },
    { label: 'Edit', value: 'edit', iconName: 'squarePen' as IconName, isShow: true },
    { label: 'Destroy', value: 'delete', iconName: 'trash' as IconName, isShow: true },
  ];

  String = String;

  ngOnInit() {
    this.loadFeatures();
  }

  loadFeatures() {
    this.botService.getFeatures()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Feature[]) => {
          this.features.set(data);
          // Default selection of first feature
          if (data && data.length > 0 && data[0].id) {
            this.onFeatureFilter(String(data[0].id));
          }
        }
      });
  }

  onFeatureFilter(featureId: string | string[]) {
    const idStr = Array.isArray(featureId) ? featureId[0] : featureId;
    const id = Number(idStr);
    this.selectedFeatureId.set(id);
    this.currentOption.feature_id = id;
    this.loadOptions(id);
  }

  loadOptions(featureId: number, silent: boolean = false) {
    this.botService.getFeatureOptions(featureId, { showLoader: !silent })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: FeatureOption[]) => {
          const transformed = data.map(o => ({
            ...o,
            display_description: o.description && o.description.length > 100
              ? o.description.substring(0, 100) + ' ...'
              : o.description || '--'
          }));
          this.options.set(transformed);
        }
      });
  }

  getSelectedFeatureName() {
    return this.features().find(f => f.id === this.selectedFeatureId())?.name || 'Unknown Feature';
  }

  saveOption() {
    const { feature_id, name, keyname, description, is_active } = this.currentOption;
    const payload: FeatureOption = { feature_id, name, keyname, description, is_active };

    const handleSuccess = () => {
      this.resetForm();
      const featureId = this.selectedFeatureId();
      if (featureId) this.loadOptions(featureId, true);
      this.actionChange.emit();
    };

    if (this.isEditMode() && this.currentOption.id) {
      this.botService.updateFeatureOption(this.currentOption.id, payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            const oldValue: any = { id: this.currentOption.id };
            const newValue: any = { id: this.currentOption.id };
            const fields: (keyof FeatureOption)[] = ['name', 'keyname', 'description', 'is_active', 'feature_id'];

            fields.forEach(field => {
              if (this.previousOptionState?.[field] !== payload[field as keyof FeatureOption]) {
                oldValue[field] = this.previousOptionState?.[field];
                newValue[field] = payload[field as keyof FeatureOption];
              }
            });

            const featureName = this.features().find(f => f.id === payload.feature_id)?.name || payload.feature_id;
            this.auditService.createAuditLog({
              entityType: AuditEntityType.PROMPT_OPTION,
              action: AuditAction.UPDATE,
              actionTitle: `Prompt Feature Option Updated`,
              oldValue,
              newValue,
              note: `Updated prompt feature option '${payload.name}' for feature '${featureName}'`
            }).subscribe({
              next: () => handleSuccess(),
              error: () => handleSuccess()
            });
          }
        });

    } else {
      this.botService.createFeatureOption(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res) => {
            const featureName = this.features().find(f => f.id === payload.feature_id)?.name || payload.feature_id;
            this.auditService.createAuditLog({
              entityType: AuditEntityType.PROMPT_OPTION,
              action: AuditAction.CREATE,
              actionTitle: `Prompt Feature Option Created`,
              oldValue: null,
              newValue: { ...payload, id: res.id, feature: { id: payload.feature_id, name: featureName } },
              note: `Created prompt feature option '${payload.name}' for feature '${featureName}'`
            }).subscribe({
              next: () => handleSuccess(),
              error: () => handleSuccess()
            });
          }
        });



    }
  }

  handleAction(event: TableActionEvent<FeatureOption>) {
    if (event.action === 'view') {
      this.openViewModal(event.row);
    } else if (event.action === 'edit') {
      this.previousOptionState = { ...event.row };
      this.currentOption = { ...event.row };
      this.isEditMode.set(true);
    } else if (event.action === 'delete') {

      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Deletion',
          description: `Are you sure you want to permanently remove variant '${event.row.name}'? Existing prompts for this variant will become inaccessible.`
        },
        panelClass: 'custom-dialog-container'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.botService.deleteFeatureOption(event.row.id!)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                const featureName = this.features().find(f => f.id === event.row.feature_id)?.name || event.row.feature_id;
                this.auditService.createAuditLog({
                  entityType: AuditEntityType.PROMPT_OPTION,
                  action: AuditAction.DELETE,
                  actionTitle: `Prompt Variant Deleted: ${event.row.name}`,
                  oldValue: { ...event.row, feature: { id: event.row.feature_id, name: featureName } },
                  note: `Deleted variant '${event.row.name}' from feature '${featureName}'`
                }).subscribe({
                  next: () => {
                    const featureId = this.selectedFeatureId();
                    if (featureId) this.loadOptions(featureId, true);
                    this.actionChange.emit();
                  },
                  error: () => {
                    const featureId = this.selectedFeatureId();
                    if (featureId) this.loadOptions(featureId, true);
                    this.actionChange.emit();
                  }
                });
              }
            });
        }
      });
    }
  }

  openViewModal(option: FeatureOption) {
    const parentFeature = this.features().find(f => f.id === option.feature_id)?.name || 'Unknown';

    const modalData: PromptDetailData = {
      title: 'Feature Variant Specification',
      fields: [
        { label: 'Parent Feature', value: parentFeature },
        { label: 'Variant Label', value: option.name },
        { label: 'System Key', value: option.keyname },
        { label: 'State', value: option.is_active ? 'Active' : 'Archived' }
      ],
      highlightLabel: 'Variant Description',
      highlightValue: option.description || 'No detailed description available.'
    };

    this.dialog.open(PromptDetailModalComponent, {
      data: modalData,
      width: '600px',
      panelClass: 'custom-modal-panel'
    });
  }

  resetForm() {
    const selectedId = this.selectedFeatureId();
    this.currentOption = this.defaultOption();
    this.previousOptionState = null;
    if (selectedId) {
      this.currentOption.feature_id = selectedId;
    }
    this.isEditMode.set(false);
  }

  private defaultOption(): FeatureOption {
    return { feature_id: 0, name: '', keyname: '', is_active: true };
  }
}
